import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { getLang, t, LANGS } from "@/lib/i18n";
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import QuestionImage from "@/components/QuestionImage";
import AudioPlayer from "@/components/AudioPlayer";

const T_LABEL = {
  pt: { name: "Português", flag: "🇵🇹" },
  en: { name: "English", flag: "🇬🇧" },
  ur: { name: "Roman Urdu", flag: "🇵🇰" },
};

export default function QuestionDetail() {
  const { qid } = useParams();
  const nav = useNavigate();
  const [lang, setL] = useState(getLang());
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [translations, setTranslations] = useState({}); // {en: {text, options, explanation}, ur: {...}}
  const [langLoading, setLangLoading] = useState({ en: true, ur: true });

  useEffect(() => {
    const h = () => setL(getLang());
    window.addEventListener("lang-change", h);
    return () => window.removeEventListener("lang-change", h);
  }, []);

  // fetch sibling list of the current question's category
  useEffect(() => {
    if (!q?.category_code) return;
    api.get(`/categories/${q.category_code}/questions`).then((r) => setSiblings(r.data)).catch(() => {});
  }, [q?.category_code]);

  const currentIdx = siblings.findIndex((s) => s.id === qid);
  const prevQ = currentIdx > 0 ? siblings[currentIdx - 1] : null;
  const nextQ = currentIdx >= 0 && currentIdx < siblings.length - 1 ? siblings[currentIdx + 1] : null;

  const loadAll = useCallback(async () => {
    setLoading(true);
    setResult(null);
    setSelected(null);
    setTranslations({});
    setLangLoading({ en: true, ur: true });
    try {
      // Primary PT load
      const r = await api.get(`/questions/${qid}?lang=pt`);
      setQ(r.data);
      setLoading(false);
      // Fire both translations in parallel (backend caches on first call)
      ["en", "ur"].forEach((lg) => {
        api.get(`/questions/${qid}?lang=${lg}`)
          .then((rr) => {
            const tr = rr.data.translations?.[lg];
            if (tr) setTranslations((prev) => ({ ...prev, [lg]: tr }));
          })
          .catch(() => {})
          .finally(() => setLangLoading((prev) => ({ ...prev, [lg]: false })));
      });
    } catch {
      setLoading(false);
    }
  }, [qid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submit = async () => {
    if (!selected) return;
    const r = await api.post(`/questions/${qid}/answer`, { letter: selected });
    setResult(r.data);
  };

  if (loading || !q) return <Layout><div className="max-w-3xl mx-auto px-6 py-16"><Loader2 className="w-6 h-6 animate-spin" /></div></Layout>;

  const renderOptions = (opts, interactive) => (
    <div className="space-y-2.5" data-testid={interactive ? "options-list" : undefined}>
      {opts.map((o) => {
        const isSelected = selected === o.letter;
        const isCorrect = result && result.correct_letter === o.letter;
        const isWrong = result && isSelected && !result.correct;
        let cls = "border-border";
        if (result) {
          if (isCorrect) cls = "border-emerald-600 bg-emerald-50/60";
          else if (isWrong) cls = "border-red-600 bg-red-50/60";
          else cls = "border-border opacity-60";
        } else if (isSelected) cls = "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/8";

        const inner = (
          <>
            <div className={`font-mono text-sm font-semibold w-6 shrink-0 ${isSelected || isCorrect ? "text-[hsl(var(--accent))]" : "text-muted-foreground"}`}>{o.letter}</div>
            <div className="flex-1 text-sm leading-relaxed">{o.text}</div>
            {result && isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />}
            {result && isWrong && <XCircle className="w-5 h-5 text-red-600 shrink-0" />}
          </>
        );

        return interactive ? (
          <button key={o.letter} data-testid={`option-${o.letter}`} disabled={!!result}
            onClick={() => setSelected(o.letter)}
            className={`w-full text-left px-5 py-3.5 border-2 rounded-md transition-colors flex items-start gap-4 ${cls}`}
          >{inner}</button>
        ) : (
          <div key={o.letter}
            className={`w-full px-5 py-3.5 border-2 rounded-md flex items-start gap-4 ${cls}`}
          >{inner}</div>
        );
      })}
    </div>
  );

  const LangBlock = ({ code, text, options, explanation, isPrimary }) => {
    const meta = T_LABEL[code];
    const isLoading = code !== "pt" && langLoading[code];
    return (
      <section className={`${isPrimary ? "" : "pt-8 mt-8 border-t border-border"}`} data-testid={`lang-block-${code}`}>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-lg">{meta.flag}</span>
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{meta.name}</span>
          </div>
          {isLoading && <Loader2 className="w-3 h-3 animate-spin text-[hsl(var(--accent))]" />}
          {isPrimary && q.audio_file && <AudioPlayer qid={q.id} hasAudio={!!q.audio_file} />}
        </div>

        {isLoading && !text ? (
          <div className="text-sm text-muted-foreground italic">Translating with Claude...</div>
        ) : text ? (
          <>
            <h2 className={`font-serif font-medium leading-tight mb-5 ${isPrimary ? "text-3xl sm:text-4xl" : "text-xl sm:text-2xl"}`}
                data-testid={isPrimary ? "question-text" : `question-text-${code}`}
                dir={code === "ur" ? "ltr" : "ltr"}>
              {text}
            </h2>
            {isPrimary && <QuestionImage qid={q.id} imageFile={q.image_file} />}
            {renderOptions(options, isPrimary)}
            {explanation && result && (
              <div className="mt-4 text-sm leading-relaxed text-muted-foreground border-l-2 border-[hsl(var(--accent))]/40 pl-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))] mb-1.5">{t(code, "explanation")}</div>
                {explanation}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground italic">Translation unavailable.</div>
        )}
      </section>
    );
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12 fade-in">
        <button onClick={() => nav(`/categories/${q.category_code}`)} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6" data-testid="back-category">
          <ArrowLeft className="w-4 h-4" /> Categoria
        </button>

        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6">
          // {q.category_code} · #{String(q.number).padStart(3, "0")}
        </div>

        {/* Portuguese — primary (interactive) */}
        <LangBlock code="pt" text={q.text_pt} options={q.options_pt} explanation={q.explanation_pt} isPrimary />

        {/* Check button / result — after PT block, before translations */}
        {!result ? (
          <Button onClick={submit} disabled={!selected} className="mt-8 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 rounded-full h-11 px-6" data-testid="check-answer-btn">
            {t(lang, "checkAnswer")}
          </Button>
        ) : (
          <div className="mt-8 border-l-2 border-[hsl(var(--accent))] pl-5 py-2 fade-in" data-testid="explanation-block">
            <div className="font-mono text-xs uppercase tracking-widest text-[hsl(var(--accent))] mb-2">{result.correct ? t(lang, "correct") : t(lang, "incorrect")} · {t(lang, "explanation")}</div>
            <p className="text-sm leading-relaxed">{q.explanation_pt || `${t(lang, "correctAnswer")}: ${result.correct_letter}`}</p>
            <Button variant="outline" onClick={() => { setResult(null); setSelected(null); }} className="mt-4 rounded-full" data-testid="try-again-btn">{t(lang, "tryAgain")}</Button>
          </div>
        )}

        {/* English translation */}
        <LangBlock
          code="en"
          text={translations.en?.text}
          options={translations.en?.options || []}
          explanation={translations.en?.explanation}
        />

        {/* Roman Urdu translation */}
        <LangBlock
          code="ur"
          text={translations.ur?.text}
          options={translations.ur?.options || []}
          explanation={translations.ur?.explanation}
        />

        {/* Prev / Next navigation */}
        <div className="mt-12 pt-6 border-t border-border flex items-center justify-between gap-4">
          <Button variant="outline" onClick={() => prevQ && nav(`/questions/${prevQ.id}`)} disabled={!prevQ}
            data-testid="prev-question-btn" className="rounded-full flex-1 sm:flex-none justify-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="truncate">{t(lang, "prev")}</span>
          </Button>
          <div className="text-xs font-mono text-muted-foreground shrink-0" data-testid="question-position">
            {currentIdx >= 0 ? `${currentIdx + 1} / ${siblings.length}` : ""}
          </div>
          <Button variant="outline" onClick={() => nextQ && nav(`/questions/${nextQ.id}`)} disabled={!nextQ}
            data-testid="next-question-btn" className="rounded-full flex-1 sm:flex-none justify-end">
            <span className="truncate">{t(lang, "next")}</span>
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </Layout>
  );
}
